import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import logoAnchor from "@/assets/Favicon.png";
import { useWhatsAppPopup } from "@/contexts/WhatsAppPopupContext";

interface NavItem {
  id: string;
  label: string;
  href: string;
  isDropdown?: boolean;
  children?: { label: string; href: string }[];
}

const NAV_ITEMS: NavItem[] = [
  { id: "inicio", label: "Início", href: "/" },
  {
    id: "servicos",
    label: "Serviços",
    href: "/#servicos",
    isDropdown: true,
    children: [
      { label: "Escola Náutica", href: "/escola" },
      { label: "Despachante", href: "/despachante" },
    ],
  },
  {
    id: "unidades",
    label: "Unidades",
    href: "/#unidades",
  },
  { id: "blog", label: "Blog", href: "/blog" },
  { id: "sobre-nos", label: "Sobre Nós", href: "/#diferenciais" },
];

// Componente para item de navegação com liquid pill
const NavPillItem = ({
  item,
  isActive,
  onClick,
}: {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  if (item.isDropdown) {
    return (
      <div
        className="relative"
        onMouseEnter={() => setDropdownOpen(true)}
        onMouseLeave={() => setDropdownOpen(false)}
      >
        <button
          onClick={() => {
            onClick();
            if (item.href && item.href !== "#") {
              const hashIndex = item.href.indexOf("#");
              if (hashIndex !== -1) {
                const hash = item.href.substring(hashIndex + 1);
                const el = document.getElementById(hash);
                if (el) {
                  el.scrollIntoView({ behavior: "smooth" });
                }
              }
            }
          }}
          className={`relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 z-10 flex items-center gap-1 whitespace-nowrap ${isActive ? "text-primary-foreground" : "text-primary-foreground/50 hover:text-primary-foreground hover:bg-white/10"
            }`}
        >
          {isActive && (
            <motion.div
              layoutId="active-nav-pill"
              className="absolute inset-0 rounded-full -z-10 shadow-lg"
              style={{
                background: "linear-gradient(135deg, hsl(43, 74%, 53%) 0%, hsl(43, 74%, 43%) 100%)",
                boxShadow: "0 4px 20px hsla(43, 74%, 43%, 0.3)",
              }}
              transition={{ type: "spring", stiffness: 250, damping: 25 }}
            />
          )}
          {item.label}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {dropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[200px] py-2 rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-white/10 shadow-xl shadow-black/30"
            >
              {item.children?.map((child) => (
                <Link
                  key={child.href}
                  to={child.href}
                  className="flex items-center gap-3 px-5 py-3 text-sm text-primary-foreground/70 hover:text-gold hover:bg-white/5 transition-colors duration-200"
                  onClick={() => setDropdownOpen(false)}
                >
                  <div className="w-1 h-1 rounded-full bg-gold/40" />
                  {child.label}
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={() => {
        onClick();
        if (item.id === "sobre-nos") {
          window.dispatchEvent(new CustomEvent("jl-active-sobre-nos"));
        }
        if (item.href === "/") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }}
      className={`relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${isActive ? "text-primary-foreground" : "text-primary-foreground/50 hover:text-primary-foreground hover:bg-white/10"
        }`}
    >
      {isActive && (
        <motion.div
          layoutId="active-nav-pill"
          className="absolute inset-0 rounded-full shadow-lg"
          style={{
            background: "linear-gradient(135deg, hsl(43, 74%, 53%) 0%, hsl(43, 74%, 43%) 100%)",
            boxShadow: "0 4px 20px hsla(43, 74%, 43%, 0.3)",
            zIndex: -1,
          }}
          transition={{ type: "spring", stiffness: 250, damping: 25 }}
        />
      )}
      <span className="relative z-10">{item.label}</span>
    </Link>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("inicio");
  const [mobileDropdowns, setMobileDropdowns] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const { openPopup } = useWhatsAppPopup();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Sincronizar aba ativa com a rota atual
  useEffect(() => {
    const path = location.pathname;
    const hash = location.hash;
    if (hash === "#servicos") {
      setActiveTab("servicos");
    } else if (hash === "#diferenciais") {
      setActiveTab("sobre-nos");
    } else if (path === "/") {
      setActiveTab("inicio");
    } else if (["/escola", "/despachante"].includes(path)) {
      setActiveTab("servicos");
    } else if (path === "/blog" || path.startsWith("/blog/")) {
      setActiveTab("blog");
    } else if (["/unidades", "/balneario-camboriu", "/sao-francisco-do-sul"].includes(path)) {
      setActiveTab("unidades");
    }
  }, [location]);

  const toggleMobileDropdown = (id: string) => {
    setMobileDropdowns((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="fixed top-2 md:top-5 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
      <motion.header
        layout
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 120, damping: 20 }}
        className={`
          pointer-events-auto
          relative flex flex-col items-center justify-between
          backdrop-blur-xl border shadow-lg
          transition-all duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)]
          ${isMobileMenuOpen
            ? "bg-primary/95 border-white/10 w-full max-w-[360px] rounded-3xl"
            : "bg-primary/50 border-white/10 w-full max-w-5xl rounded-full"
          }
          ${scrolled && !isMobileMenuOpen
            ? "py-2 px-4 bg-primary/70 border-gold/10 scale-[0.98] shadow-gold/5"
            : "py-3 px-6"
          }
        `}
      >
        <div className="w-full flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center pl-2 group">
            <img
              src={logoAnchor}
              alt="JL Escola Náutica"
              className="h-10 w-auto transition-transform duration-300 group-hover:scale-110"
            />
          </Link>

          {/* Desktop Nav - Liquid Pill Navigation */}
          {!isMobileMenuOpen && (
            <nav className="hidden md:flex items-center gap-1 bg-white/5 p-1 rounded-full border border-white/5 backdrop-blur-md">
              {NAV_ITEMS.map((item) => (
                <NavPillItem
                  key={item.id}
                  item={item}
                  isActive={activeTab === item.id}
                  onClick={() => setActiveTab(item.id)}
                />
              ))}
            </nav>
          )}

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => openPopup("generic", "phone")}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-emerald-400 border border-emerald-400/20 hover:bg-emerald-400/10 transition-all duration-200"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">Fale Conosco</span>
            </button>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/atendimento-vip"
                className="relative inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-sm overflow-hidden text-primary-foreground shadow-lg transition-all"
                style={{
                  background: "linear-gradient(135deg, hsl(43, 74%, 53%) 0%, hsl(43, 74%, 43%) 100%)",
                  boxShadow: "0 4px 15px hsla(43, 74%, 43%, 0.25)",
                }}
              >
                Atendimento VIP
              </Link>
            </motion.div>
          </div>

          {/* Mobile: Fale Conosco (center, hidden when menu open) */}
          {!isMobileMenuOpen && (
            <button
              onClick={() => openPopup("generic", "phone")}
              className="md:hidden flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-emerald-400 border border-emerald-400/20 hover:bg-emerald-400/10 transition-all duration-200"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>Fale Conosco</span>
            </button>
          )}

          {/* Mobile Toggle */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-full hover:bg-white/10 text-gold transition-colors"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Content */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="w-full overflow-hidden"
            >
              <div className="flex flex-col gap-1 pt-6 pb-4">
                {NAV_ITEMS.map((item) => (
                  <div key={item.id}>
                    {item.isDropdown ? (
                      <>
                        <button
                          onClick={() => toggleMobileDropdown(item.id)}
                          className="w-full flex items-center justify-between px-4 py-3 rounded-2xl hover:bg-white/5 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                        >
                          <span className="font-medium">{item.label}</span>
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-200 ${mobileDropdowns[item.id] ? "rotate-180" : ""
                              }`}
                          />
                        </button>
                        <AnimatePresence>
                          {mobileDropdowns[item.id] && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              {item.children?.map((child) => (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className="flex items-center gap-3 pl-8 pr-4 py-2.5 text-sm text-primary-foreground/50 hover:text-gold transition-colors"
                                >
                                  <div className="w-1 h-1 rounded-full bg-gold/30" />
                                  {child.label}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={(e) => {
                          setIsMobileMenuOpen(false);
                          if (item.id === "sobre-nos") {
                            window.dispatchEvent(new CustomEvent("jl-active-sobre-nos"));
                          }
                          // Handle hash links (e.g. /#unidades)
                          const hashIndex = item.href.indexOf("#");
                          if (hashIndex !== -1) {
                            const hash = item.href.substring(hashIndex + 1);
                            // If we're already on the home page, just scroll
                            if (location.pathname === "/") {
                              e.preventDefault();
                              const el = document.getElementById(hash);
                              if (el) {
                                setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 350);
                              }
                            }
                            // If on another page, Link will navigate to "/" and then we scroll
                            else {
                              setTimeout(() => {
                                const el = document.getElementById(hash);
                                if (el) {
                                  el.scrollIntoView({ behavior: "smooth" });
                                }
                              }, 600);
                            }
                          } else if (item.href === "/") {
                            // Scroll to top on home
                            if (location.pathname === "/") {
                              e.preventDefault();
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }
                          }
                        }}
                        className="flex items-center justify-between px-4 py-3 rounded-2xl hover:bg-white/5 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                      >
                        <span className="font-medium">{item.label}</span>
                        <ArrowRight size={16} className="opacity-50" />
                      </Link>
                    )}
                  </div>
                ))}

                <div className="h-px w-full bg-white/10 my-3" />

                <button
                  onClick={() => { openPopup("generic", "phone"); setIsMobileMenuOpen(false); }}
                  className="flex items-center justify-center gap-2 mx-2 py-3 rounded-2xl text-emerald-400 border border-emerald-400/20 font-medium text-sm transition-colors hover:bg-emerald-400/10"
                >
                  <Phone className="w-4 h-4" />
                  Fale Conosco
                </button>

                <Link
                  to="/atendimento-vip"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center mx-2 mt-2 py-3 rounded-2xl font-medium text-sm text-primary-foreground shadow-lg"
                  style={{
                    background: "linear-gradient(135deg, hsl(43, 74%, 53%) 0%, hsl(43, 74%, 43%) 100%)",
                  }}
                >
                  Atendimento VIP
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </div>
  );
};

export default Header;
